"use client"

import Link from "next/link"
import { ArrowRight, Bot } from "lucide-react"
import { indexVar } from "@/components/marketing/landing-v2/cssVars"
import { useReveal } from "@/components/marketing/landing-v2/useReveal"
import {
  buildWorkflowNodes,
  WorkflowRailMini,
} from "@/components/marketing/landing-v2/WorkflowRail"
import { MODULE_CATEGORY_IDS } from "@/lib/landing-v2/content"
import type { Landing2Copy } from "@/lib/landing-v2/copy"

export function AutomationModules({ copy }: { copy: Landing2Copy }) {
  const headRef = useReveal<HTMLElement>()
  const gridRef = useReveal<HTMLDivElement>()

  return (
    <section className="l2-section" id="modules">
      <div className="l2-container">
        <header className="l2-section-head" ref={headRef}>
          <span className="l2-label">{copy.modules.label}</span>
          <h2 className="l2-h2">{copy.modules.title}</h2>
          <p className="l2-section-sub">{copy.modules.subtitle}</p>
        </header>

        <div className="l2-module-grid l2-stagger" ref={gridRef}>
          {MODULE_CATEGORY_IDS.map((categoryId, index) => {
            const module = copy.modulesContent[categoryId]
            const nodes = buildWorkflowNodes(copy, categoryId)

            return (
              <article className="l2-module" key={categoryId} style={indexVar(index)}>
                <div className="l2-module-top">
                  <span className="l2-label">{module.tag}</span>
                  <span className="l2-status l2-status-quiet">
                    <i />
                    {copy.modules.status}
                  </span>
                </div>

                <div className="l2-module-copy">
                  <h3 className="l2-module-title">{module.title}</h3>
                  <p className="l2-module-benefit">{module.benefit}</p>
                </div>

                <WorkflowRailMini nodes={nodes} />

                <dl className="l2-module-io">
                  <div className="l2-module-io-row">
                    <dt className="l2-label">{copy.modules.inputs}</dt>
                    <dd>
                      <span className="l2-chips">
                        {module.inputs.map((input) => (
                          <span className="l2-chip" key={input}>
                            {input}
                          </span>
                        ))}
                      </span>
                    </dd>
                  </div>
                  <div className="l2-module-io-row">
                    <dt className="l2-label">{copy.modules.output}</dt>
                    <dd className="l2-module-output">{module.output}</dd>
                  </div>
                </dl>
              </article>
            )
          })}

          <Link className="l2-module l2-module-agents" href="/agents" style={indexVar(MODULE_CATEGORY_IDS.length)}>
            <span className="l2-module-agents-icon" aria-hidden="true">
              <Bot size={22} strokeWidth={1.75} />
            </span>
            <span className="l2-module-copy">
              <span className="l2-label">{copy.modules.agents.label}</span>
              <span className="l2-module-title">{copy.modules.agents.title}</span>
              <span className="l2-module-benefit">{copy.modules.agents.body}</span>
            </span>
            <span className="l2-button l2-button-sm">
              {copy.modules.agents.cta}
              <ArrowRight size={15} strokeWidth={2.2} />
            </span>
          </Link>
        </div>
      </div>
    </section>
  )
}
